import React, { useId } from "react";
import { WARDROBE_SWATCHES, lookupHex, lookupName } from "../data/designOptions";
import { MaterialStrip } from "./KitchenMockup";

const WardrobeMockup = ({ config, className = "" }) => {
  const uid = useId().replace(/:/g, "");
  const laminate = lookupHex(WARDROBE_SWATCHES.laminate, config.laminate, "#7B5F4A");
  const wood = lookupHex(WARDROBE_SWATCHES.wood, config.wood, "#C9A57A");
  const handle = lookupHex(WARDROBE_SWATCHES.handle, config.handle, "#B8996F");
  const wall = lookupHex(WARDROBE_SWATCHES.wall, config.wall, "#EAE2D2");
  const glass = lookupHex(WARDROBE_SWATCHES.glass, config.glass, "transparent");
  const isSliding = config.doorStyle === "sliding";
  const isRecessed = config.handle === "recessed";

  const cd = (h, p) => shade(h, -p);
  const cl = (h, p) => shade(h, p);
  const id = (s) => `w${uid}${s}`;

  return (
    <div className={`relative w-full ${className}`} data-testid="wardrobe-svg-mockup">
      <svg
        viewBox="0 0 1200 760"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", width: "100%", height: "auto" }}
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          {/* Wall ambient */}
          <radialGradient id={id("Amb")} cx="50%" cy="0%" r="100%">
            <stop offset="0%" stopColor={cl(wall, 16)} />
            <stop offset="60%" stopColor={wall} />
            <stop offset="100%" stopColor={cd(wall, 22)} />
          </radialGradient>
          <linearGradient id={id("Side")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={cd(wall, 30)} />
            <stop offset="100%" stopColor={cd(wall, 14)} />
          </linearGradient>

          {/* Floor wood */}
          <linearGradient id={id("Floor")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={cl(wood, 6)} />
            <stop offset="60%" stopColor={cd(wood, 14)} />
            <stop offset="100%" stopColor={cd(wood, 38)} />
          </linearGradient>
          <linearGradient id={id("FloorSheen")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.08)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>

          {/* Wood grain procedural for floor */}
          <filter id={id("FloorTex")} x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="turbulence" baseFrequency="0.008 0.42" numOctaves="2" seed="5" />
            <feColorMatrix type="matrix" values={`
              0 0 0 0 ${cTo(cd(wood, 50))}
              0 0 0 0 ${cTo(cd(wood, 42))}
              0 0 0 0 ${cTo(cd(wood, 40))}
              0 0 0 0.45 0`} />
          </filter>

          {/* Laminate face — multi-stop for a "matte plank" look */}
          <linearGradient id={id("LamFace")} x1="0" y1="0" x2="0.12" y2="1">
            <stop offset="0%" stopColor={cl(laminate, 20)} />
            <stop offset="48%" stopColor={cl(laminate, 4)} />
            <stop offset="52%" stopColor={cd(laminate, 4)} />
            <stop offset="100%" stopColor={cd(laminate, 22)} />
          </linearGradient>
          <linearGradient id={id("LamSide")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={cd(laminate, 38)} />
            <stop offset="100%" stopColor={cd(laminate, 20)} />
          </linearGradient>
          <linearGradient id={id("LamTop")} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={cl(laminate, 22)} />
            <stop offset="100%" stopColor={cd(laminate, 4)} />
          </linearGradient>

          {/* Wood-grain texture on laminate if "walnut/oak/teak" */}
          <filter id={id("LamTex")} x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="turbulence" baseFrequency="0.01 0.5" numOctaves="2" seed="9" />
            <feColorMatrix type="matrix" values={`
              0 0 0 0 ${cTo(cd(laminate, 38))}
              0 0 0 0 ${cTo(cd(laminate, 32))}
              0 0 0 0 ${cTo(cd(laminate, 30))}
              0 0 0 0.32 0`} />
          </filter>

          {/* Door shine highlight */}
          <linearGradient id={id("Shine")} x1="0" y1="0" x2="0.25" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.32)" />
            <stop offset="35%" stopColor="rgba(255,255,255,0.06)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>

          {/* Brass */}
          <linearGradient id={id("Brass")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={cl(handle, 26)} />
            <stop offset="48%" stopColor={cl(handle, 8)} />
            <stop offset="52%" stopColor={cd(handle, 4)} />
            <stop offset="100%" stopColor={cd(handle, 22)} />
          </linearGradient>

          {/* Window light */}
          <linearGradient id={id("Light")} x1="0" y1="0" x2="0.4" y2="1">
            <stop offset="0%" stopColor="rgba(255,235,190,0.22)" />
            <stop offset="100%" stopColor="rgba(255,235,190,0)" />
          </linearGradient>

          {/* Lamp glow */}
          <radialGradient id={id("Lamp")} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,220,160,0.55)" />
            <stop offset="100%" stopColor="rgba(255,220,160,0)" />
          </radialGradient>

          {/* Shadows */}
          <filter id={id("AO")} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="9" />
            <feOffset dx="0" dy="12" />
            <feComponentTransfer><feFuncA type="linear" slope="0.6" /></feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={id("Blur")}><feGaussianBlur stdDeviation="14" /></filter>

          {/* Film grain */}
          <filter id={id("Grain")} x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="11" />
            <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.08 0" />
          </filter>

          <radialGradient id={id("Vig")} cx="50%" cy="55%" r="75%">
            <stop offset="60%" stopColor="rgba(0,0,0,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.4)" />
          </radialGradient>
        </defs>

        {/* ROOM */}
        <polygon points="120,40 1080,40 1080,540 120,540" fill={`url(#${id("Amb")})`} />
        <polygon points="0,0 120,40 120,640 0,720" fill={`url(#${id("Side")})`} />
        <polygon points="1200,0 1080,40 1080,640 1200,720" fill={`url(#${id("Side")})`} transform="scale(-1,1) translate(-1200,0)" />
        <polygon points="0,720 1200,720 1080,540 120,540" fill={`url(#${id("Floor")})`} />
        <rect x="0" y="540" width="1200" height="180" fill={cd(wood, 35)} filter={`url(#${id("FloorTex")})`} opacity="0.5" />
        <polygon points="0,720 1200,720 1080,540 120,540" fill={`url(#${id("FloorSheen")})`} />
        {/* Plank lines perspective */}
        {[140, 280, 420, 560, 700, 840, 980, 1080].map((x) => (
          <line key={`fp-${x}`} x1={x} y1="720" x2={120 + (x / 1200) * 960} y2="540" stroke={cd(wood, 40)} strokeOpacity="0.4" strokeWidth="1" />
        ))}

        {/* WINDOW with light cone */}
        <g>
          <rect x="170" y="100" width="200" height="220" fill="#0e0d0b" />
          <rect x="184" y="116" width="172" height="188" fill="#d2c8b1" opacity="0.7" />
          <line x1="270" y1="116" x2="270" y2="304" stroke="#0e0d0b" strokeWidth="3" />
          <line x1="184" y1="210" x2="356" y2="210" stroke="#0e0d0b" strokeWidth="3" />
          <polygon points="184,304 356,304 1080,640 240,640" fill={`url(#${id("Light")})`} opacity="0.7" />
        </g>

        {/* Wall art */}
        <g opacity="0.95">
          <rect x="200" y="380" width="160" height="120" fill={cd(wall, 35)} />
          <rect x="210" y="390" width="140" height="100" fill={cd(laminate, 12)} />
        </g>

        {/* BED partial */}
        <g filter={`url(#${id("AO")})`}>
          <rect x="100" y="560" width="320" height="100" fill={cd(wood, 18)} />
          <polygon points="420,560 460,540 460,640 420,660" fill={cd(wood, 28)} />
          <rect x="100" y="510" width="320" height="60" fill={cl(wall, 22)} />
          <polygon points="420,510 460,490 460,540 420,560" fill={cl(wall, 8)} />
          <rect x="120" y="475" width="120" height="50" rx="8" fill={cl(wall, 30)} />
          <rect x="120" y="475" width="120" height="6" rx="8" fill={cl(wall, 40)} />
          <rect x="260" y="475" width="120" height="50" rx="8" fill={cl(wall, 30)} />
          <rect x="260" y="475" width="120" height="6" rx="8" fill={cl(wall, 40)} />
          <polygon points="100,605 420,605 420,640 100,640" fill={cd(laminate, 14)} opacity="0.85" />
        </g>

        {/* WARDROBE */}
        <g data-testid="wardrobe-cabinet" filter={`url(#${id("AO")})`}>
          {/* Top */}
          <polygon points="440,90 1080,90 1100,108 460,108" fill={`url(#${id("LamTop")})`} />
          {/* Right side */}
          <polygon points="1080,90 1100,108 1100,608 1080,610" fill={`url(#${id("LamSide")})`} />
          {/* Plinth */}
          <rect x="440" y="600" width="640" height="10" fill={cd(laminate, 45)} />
          <polygon points="1080,600 1100,608 1100,618 1080,610" fill={cd(laminate, 55)} />
          {/* Carcass back */}
          <rect x="440" y="108" width="640" height="492" fill={cd(laminate, 35)} />

          {isSliding ? (
            <g data-testid="wardrobe-door-sliding">
              <rect x="440" y="108" width="640" height="6" fill={cd(laminate, 52)} />
              <rect x="440" y="592" width="640" height="6" fill={cd(laminate, 52)} />
              {[
                { x: 445, w: 220, layer: "back" },
                { x: 670, w: 220, layer: "front" },
                { x: 855, w: 220, layer: "back" },
              ].map((p) => (
                <g key={`sd-${p.x}`}>
                  {p.layer === "front" && (
                    <rect x={p.x - 6} y="118" width="6" height="470" fill="rgba(0,0,0,0.55)" filter={`url(#${id("Blur")})`} />
                  )}
                  <rect x={p.x} y="118" width={p.w} height="470" fill={`url(#${id("LamFace")})`} />
                  {/* Procedural wood grain overlay on laminate */}
                  <rect x={p.x} y="118" width={p.w} height="470" fill={cl(laminate, 5)} filter={`url(#${id("LamTex")})`} opacity="0.55" />
                  {glass !== "transparent" && (
                    <g>
                      <rect x={p.x + 10} y="130" width={p.w - 20} height="170" fill={glass} />
                      <rect x={p.x + 10} y="130" width={p.w - 20} height="170" fill={`url(#${id("Shine")})`} opacity="0.5" />
                    </g>
                  )}
                  <rect x={p.x} y="118" width={p.w} height="470" fill={`url(#${id("Shine")})`} opacity="0.42" />
                  <rect
                    x={p.x + 12}
                    y="130"
                    width={p.w - 24}
                    height="446"
                    fill="none"
                    stroke={cd(laminate, 50)}
                    strokeOpacity="0.6"
                    strokeWidth="1"
                  />
                  {!isRecessed ? (
                    <rect x={p.x + p.w - 24} y="300" width="6" height="80" rx="2" fill={`url(#${id("Brass")})`} />
                  ) : (
                    <rect x={p.x + p.w - 18} y="280" width="2" height="120" fill={cd(laminate, 60)} />
                  )}
                </g>
              ))}
            </g>
          ) : (
            <g data-testid="wardrobe-door-hinged">
              {[
                { x: 450, w: 152 },
                { x: 612, w: 152 },
                { x: 774, w: 152 },
                { x: 924, w: 146 },
              ].map((p, i) => (
                <g key={`hd-${p.x}`}>
                  <rect x={p.x} y="118" width={p.w} height="478" fill={`url(#${id("LamFace")})`} />
                  <rect x={p.x} y="118" width={p.w} height="478" fill={cl(laminate, 5)} filter={`url(#${id("LamTex")})`} opacity="0.55" />
                  {glass !== "transparent" && (
                    <g>
                      <rect x={p.x + 10} y="134" width={p.w - 20} height="180" fill={glass} />
                      <rect x={p.x + 10} y="134" width={p.w - 20} height="180" fill={`url(#${id("Shine")})`} opacity="0.5" />
                    </g>
                  )}
                  <rect x={p.x} y="118" width={p.w} height="478" fill={`url(#${id("Shine")})`} opacity="0.4" />
                  <rect
                    x={p.x + 8}
                    y="128"
                    width={p.w - 16}
                    height="458"
                    fill="none"
                    stroke={cd(laminate, 50)}
                    strokeOpacity="0.6"
                    strokeWidth="1"
                  />
                  {!isRecessed && (
                    <rect
                      x={p.x + (i % 2 === 0 ? p.w - 16 : 10)}
                      y="290"
                      width="6"
                      height="120"
                      rx="2"
                      fill={`url(#${id("Brass")})`}
                    />
                  )}
                  <rect
                    x={p.x + (i % 2 === 0 ? 0 : p.w - 1)}
                    y="118"
                    width="1"
                    height="478"
                    fill="rgba(0,0,0,0.45)"
                  />
                </g>
              ))}
            </g>
          )}

          {/* Window light beam onto wardrobe */}
          <rect x="440" y="118" width="280" height="470" fill={`url(#${id("Light")})`} opacity="0.55" pointerEvents="none" />
        </g>

        {/* Floor contact shadow */}
        <ellipse cx="770" cy="624" rx="370" ry="11" fill="black" opacity="0.55" filter={`url(#${id("Blur")})`} />

        {/* FLOOR LAMP */}
        <g>
          <line x1="1130" y1="220" x2="1130" y2="640" stroke="#1f1a16" strokeWidth="3" />
          <polygon points="1095,220 1165,220 1150,160 1110,160" fill={`url(#${id("Brass")})`} />
          <ellipse cx="1130" cy="640" rx="22" ry="6" fill={cd(wood, 28)} />
          <ellipse cx="1130" cy="186" rx="80" ry="80" fill={`url(#${id("Lamp")})`} opacity="0.7" />
        </g>

        {/* Plant */}
        <g>
          <rect x="1030" y="600" width="42" height="48" rx="4" fill={cd(wall, 18)} />
          <ellipse cx="1051" cy="600" rx="40" ry="14" fill="#3a4a32" />
          <ellipse cx="1040" cy="592" rx="14" ry="8" fill="#4c5e40" />
          <ellipse cx="1062" cy="594" rx="14" ry="8" fill="#3e503a" />
        </g>

        {/* Vignette */}
        <rect x="0" y="0" width="1200" height="760" fill={`url(#${id("Vig")})`} pointerEvents="none" />
        {/* Film grain */}
        <rect x="0" y="0" width="1200" height="760" filter={`url(#${id("Grain")})`} pointerEvents="none" opacity="0.5" />
      </svg>

      <MaterialStrip
        items={[
          { label: "Laminate", name: lookupName(WARDROBE_SWATCHES.laminate, config.laminate), tone: laminate },
          { label: "Door", name: isSliding ? "Sliding" : "Hinged" },
          { label: "Glass", name: lookupName(WARDROBE_SWATCHES.glass, config.glass), tone: glass === "transparent" ? null : glass },
          { label: "Wood", name: lookupName(WARDROBE_SWATCHES.wood, config.wood), tone: wood },
          { label: "Handle", name: lookupName(WARDROBE_SWATCHES.handle, config.handle), tone: handle },
          { label: "Wall", name: lookupName(WARDROBE_SWATCHES.wall, config.wall), tone: wall },
        ]}
      />
    </div>
  );
};

function shade(hex, percent) {
  if (!hex || !hex.startsWith("#")) return hex;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const num = parseInt(h, 16);
  let r = (num >> 16) + percent;
  let g = ((num >> 8) & 0x00ff) + percent;
  let b = (num & 0x0000ff) + percent;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function cTo(hex) {
  if (!hex || !hex.startsWith("#")) return "0";
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return (parseInt(h.slice(0, 2), 16) / 255).toFixed(3);
}

export default WardrobeMockup;
